import * as React from 'react';
import useStyles from './use_styles';
import { ContentType, FooterInterface, ItemType, LinkType } from './types';
import propTypes from './prop_types';

import { useTheme } from '@veneer/theme';
import highContrastMediaQuery from '../utils/high_contrast_media_query';

const Footer = React.forwardRef<
  HTMLElement,
  FooterInterface &
    Omit<React.HTMLAttributes<HTMLElement>, keyof FooterInterface>
>(
  (
    {
      appearance = 'minimal',
      className,
      customStyle,
      id,
      mainContent = {},
      secondaryContent,
      ...footerProps
    },
    ref
  ) => {
    const theme = useTheme();
    const isHc = theme.mode === 'contrast';

    const linkClassName = 'vn-footer__link';

    const style = useStyles();

    const cloneLinkElement = (
      element: React.ReactNode,
      label: React.ReactNode | undefined
    ) =>
      React.isValidElement<React.HTMLAttributes<HTMLElement>>(element)
        ? React.cloneElement(
            element,
            {
              className: linkClassName,
              tabIndex: 0,
            },
            label
          )
        : element;

    const linkElement = (link: LinkType) => {
      if (link.url) {
        return (
          <a className={linkClassName} href={link.url}>
            {link.label}
          </a>
        );
      }
      if (link.element) {
        return cloneLinkElement(link.element, link.label);
      }
      return null;
    };

    const footerContent = (content: ContentType, isSecondary = false) => {
      const { leadingArea, links, trailingArea } = content;

      const hasSecondary = appearance === 'secondary';
      const isMinimalCentered = appearance === 'minimalcentered';
      const isTiered = appearance === 'tiered' || hasSecondary;
      const isUltraMinimal = appearance === 'ultraminimal';

      return (
        <div
          css={[
            style.root.base,
            isUltraMinimal && style.root.ultraMinimal,
            hasSecondary && style.root.hasSecondary,
            secondaryContent && [
              style.rootSecondary.base,
              isHc && style.rootSecondary.hc,
            ],
            isMinimalCentered && style.root.minimalCentered,
            !isMinimalCentered && [
              style.root.notMinimalCentered.base,
              isTiered && style.root.notMinimalCentered.tiered,
            ],
            isHc && style.root.hc,
            highContrastMediaQuery(theme),
            customStyle,
          ]}
        >
          {leadingArea && (
            <div
              css={[
                style.leadingArea.base,
                isMinimalCentered && style.leadingArea.minimalCentered,
              ]}
            >
              {leadingArea}
            </div>
          )}
          {links && (
            <nav
              css={[
                style.links.base,
                isMinimalCentered && style.links.minimalCentered,
                !secondaryContent &&
                  !isUltraMinimal && [
                    style.linkPrimary.base,
                    isHc && style.linkPrimary.hc,
                  ],
                mainContent.linksCustomStyle,
              ]}
            >
              <ul>
                {links.map((link: LinkType) => {
                  const hasNestedLinks =
                    (isTiered || hasSecondary) &&
                    !isSecondary &&
                    link.items?.length;

                  if (hasNestedLinks) {
                    return (
                      <li>
                        {linkElement(link)}
                        <ul>
                          {link.items!.map((item: ItemType) => (
                            <li key={item.id}>{linkElement(item)}</li>
                          ))}
                        </ul>
                      </li>
                    );
                  }

                  return <li key={link.id}>{linkElement(link)}</li>;
                })}
              </ul>
            </nav>
          )}
          {trailingArea && (
            <div
              css={[
                style.trailingArea.base,
                isMinimalCentered && style.trailingArea.minimalCentered,
              ]}
            >
              {trailingArea}
            </div>
          )}
        </div>
      );
    };

    return (
      <footer className={className} id={id} ref={ref} {...footerProps}>
        {footerContent(mainContent)}
        {secondaryContent && footerContent(secondaryContent, true)}
      </footer>
    );
  }
);

Footer.propTypes = propTypes;

Footer.displayName = 'Footer';

export default Footer;
